import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import NoData from '../../../components/common/NoData';
import StatusBadge from '../../../components/ui/StatusBadge';
import { getUserClassroomAssignmentById, getUserClassroomAssignmentsQuestions, submitUserClassroomAssignmentResponse, updateClassroomAssignmentVideoProgress } from '../../../services/user/classroom.services';
import { decodeData, encodeData } from '../../../utils/encodeAndEncode';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { getSignedMediaUrl } from '../../../services/media.service';
import BitmovinPlayer from '../../../components/common/BitmovinPlayer';
import useClassroomRealtime from '../../../hooks/useClassroomRealtime';
import { CLASSROOM_REALTIME_EVENTS } from '../../../services/classroomRealtime.service';
import { scheduleAssignmentDueTimer } from '../../../utils/classroomScheduleTimers';
import { useTimezone } from '../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../utils/timezone';

function AssignmentQuestions() {
    const { timeZone } = useTimezone();
    const [selectedFiles, setSelectedFiles] = useState({});
    /** Per-question message when picked file fails validation (shown under upload control). */
    const [fileUploadHint, setFileUploadHint] = useState({});
    const [answerTexts, setAnswerTexts] = useState({});
    const [questions, setQuestions] = useState([]);
    const [availability, setAvailability] = useState('checking');
    const availabilityRequest = useRef(0);
    const [submittedQuestions, setSubmittedQuestions] = useState(new Set());
    const [dueDate, setDueDate] = useState(null);
    const [, setIsSubmissionLocked] = useState(false);
    const [videoProgressStatus, setVideoProgressStatus] = useState({});
    const [videoProgressLoading, setVideoProgressLoading] = useState({});
    const [reportedMilestones, setReportedMilestones] = useState({}); // Track which milestones have been reported

    const { encodedAssignmentId } = useParams();
    const [searchParams] = useSearchParams();
    const encodedClassroomId = searchParams.get('classroomid');
    const navigate = useNavigate();

    // Format due date
    const formatDueDate = (dueDate) => {
        if (!dueDate) return 'No due date';
        return formatUtcInTimeZone(dueDate, timeZone, 'MMM D, YYYY, hh:mm A');
    };

    // Check if due date has passed
    const checkDueDatePassed = (dueDateValue) => {
        if (!dueDateValue) return false;
        const date = new Date(dueDateValue);
        if (isNaN(date.getTime())) return false;
        return date < new Date();
    };

    const assignment_id = decodeData(decodeURIComponent(encodedAssignmentId));
    const classroom_id = encodedClassroomId ? decodeData(decodeURIComponent(encodedClassroomId)) : null;
    const user_id = JSON.parse(localStorage.getItem('user'))?.id;

    const checkAvailability = async () => {
        const requestId = availabilityRequest.current;
        try {
            const response = await getUserClassroomAssignmentById({ class_id: classroom_id, assignment_id });
            if (requestId !== availabilityRequest.current) return false;
            if (!response?.success) throw new Error('This assignment is not available');
            setAvailability('available');
            return true;
        } catch (error) {
            if (requestId !== availabilityRequest.current) return false;
            setAvailability('unavailable');
            setQuestions([]);
            return false;
        }
    };


    // Fetch Questions
    const fetchQuestions = async () => {
        try {
            const response = await getUserClassroomAssignmentsQuestions({
                assignment_id,
                classroom_id,
                user_id
            });


            if (response.success) {
                // Backend successResponse: data = { due_date, data: questionsWithSubmissions }
                const questionsList = Array.isArray(response.data) ? response.data : (response.data?.data || []);
                const dueDateValue = response.due_date ?? response.data?.due_date;
                const hydratedQuestions = await Promise.all(
                    questionsList.map(async (question) => {
                        let questionMediaUrl = question.media_url || null;
                        if (!questionMediaUrl && question.media_id) {
                            questionMediaUrl = await getSignedMediaUrl({
                                type: 'resource',
                                mediaId: question.media_id,
                                kind: 'assignment_question'
                            });
                        }

                        let submissionMediaUrl = question.submission_media_url || null;
                        if (!submissionMediaUrl && question.submission_media_id) {
                            submissionMediaUrl = await getSignedMediaUrl({
                                type: 'resource',
                                mediaId: question.submission_media_id,
                                kind: 'assignment_submission'
                            });
                        }

                        return {
                            ...question,
                            media_url: questionMediaUrl,
                            submission_media_url: submissionMediaUrl
                        };
                    })
                );

                setQuestions(hydratedQuestions);
                setDueDate(dueDateValue); // Store the due date
                setIsSubmissionLocked(checkDueDatePassed(dueDateValue));

                const progressMap = {};
                const milestonesMap = {};
                hydratedQuestions.forEach((question) => {
                    if (question.question_type === 'video') {
                        const progress = question.video_progress ?? 0;
                        progressMap[question.id] = progress;

                        // Mark milestones as already reported based on current progress
                        if (progress >= 30) milestonesMap[`${question.id}_30`] = true;
                        if (progress >= 60) milestonesMap[`${question.id}_60`] = true;
                        if (progress >= 100) milestonesMap[`${question.id}_100`] = true;
                    }
                });
                setVideoProgressStatus(progressMap);
                setReportedMilestones(milestonesMap);
            }

        } catch (error) {
            console.error("Error fetching questions:", error);
            if (error.message?.toLowerCase().includes('not available')) {
                setAvailability('unavailable');
                setQuestions([]);
            }
        }
    };

    useEffect(() => {
        availabilityRequest.current += 1;
        setAvailability('checking');
        setQuestions([]);
        if (assignment_id && classroom_id && user_id) {
            checkAvailability().then((available) => {
                if (available) fetchQuestions();
            });
        } else {
            setAvailability('unavailable');
        }
    }, [assignment_id, classroom_id, user_id]);

    useEffect(() => {
        if (!assignment_id || !classroom_id) return undefined;
        const refresh = () => {
            if (document.visibilityState === 'visible') {
                checkAvailability().then((available) => {
                    if (available && availability === 'unavailable') fetchQuestions();
                });
            }
        };
        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', refresh);
        const timer = window.setInterval(refresh, 30000);
        return () => {
            window.removeEventListener('focus', refresh);
            document.removeEventListener('visibilitychange', refresh);
            window.clearInterval(timer);
        };
    }, [assignment_id, classroom_id, availability]);

    useEffect(() => {
        setIsSubmissionLocked(checkDueDatePassed(dueDate));
    }, [dueDate]);

    useClassroomRealtime(classroom_id, {
        [CLASSROOM_REALTIME_EVENTS.ASSIGNMENT_VISIBILITY_CHANGED]: (payload) => {
            if (Number(payload.assignmentId ?? payload.assignment_id) === Number(assignment_id) && Number(payload.is_visible) === 0) {
                availabilityRequest.current += 1;
                setAvailability('unavailable');
                setQuestions([]);
            }
        },
        [CLASSROOM_REALTIME_EVENTS.ASSIGNMENT_DUE_REACHED]: (payload) => {
            if (Number(payload.assignmentId) === Number(assignment_id)) {
                setIsSubmissionLocked(true);
            }
        },
        [CLASSROOM_REALTIME_EVENTS.ASSIGNMENT_UPDATED]: (payload) => {
            if (Number(payload.assignmentId ?? payload.assignment_id) !== Number(assignment_id)) return;
            if (payload.due_date) {
                setDueDate(payload.due_date);
            }
            if (Number(payload.is_visible) === 0) {
                availabilityRequest.current += 1;
                setIsSubmissionLocked(true);
                setAvailability('unavailable');
                setQuestions([]);
            }
        },
    });

    useEffect(() => {
        if (!assignment_id || !dueDate) return undefined;
        return scheduleAssignmentDueTimer(assignment_id, dueDate, () => {
            setIsSubmissionLocked(true);
        });
    }, [assignment_id, dueDate]);

    const audioExtensionOk = (name) => {
        const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
        return ['mp3', 'wav', 'm4a', 'aac', 'ogg', 'webm', 'flac', 'opus', 'mpeg', 'mp4'].includes(ext);
    };

    const isAcceptedMediaType = (file, type) => {
        const mime = (file.type || '').toLowerCase();
        if (type === 'image') return mime.startsWith('image/');
        if (type === 'audio') {
            // Many phones / recorders use mp4 container (MIME video/mp4) or webm for voice notes
            return (
                mime.startsWith('audio/')
                || mime === 'video/webm'
                || mime === 'video/mp4'
                || mime === 'audio/mp4'
                || mime === 'audio/x-m4a'
                || mime === 'video/quicktime'
                || (!mime && audioExtensionOk(file.name))
            );
        }
        if (type === 'video') return mime.startsWith('video/');
        return false;
    };

    // Handle File Upload
    const handleFileUpload = (event, type, questionId) => {
        if (submittedQuestions.has(questionId)) return;

        const file = event.target.files[0];
        if (file) {
            if (!isAcceptedMediaType(file, type)) {
                console.warn('[AssignmentQuestions] File rejected: wrong or missing MIME for expected type', {
                    questionId,
                    expectedType: type,
                    fileName: file.name,
                    fileType: file.type || '(empty)',
                });
                setFileUploadHint((prev) => ({
                    ...prev,
                    [questionId]:
                        type === 'audio'
                            ? 'Use an audio file (e.g. mp3, m4a, wav, ogg) or MP4/M4A from your device. This file type was not accepted.'
                            : type === 'video'
                              ? 'Please choose a video file.'
                              : 'Please choose an image file.',
                }));
                event.target.value = '';
                return;
            }
            setFileUploadHint((prev) => {
                const next = { ...prev };
                delete next[questionId];
                return next;
            });
            setSelectedFiles((prev) => {
                const prevEntry = prev[questionId];
                if (prevEntry?.preview) {
                    URL.revokeObjectURL(prevEntry.preview);
                }
                const needsPreview = type === 'image' || type === 'video' || type === 'audio';
                return {
                    ...prev,
                    [questionId]: {
                        file,
                        preview: needsPreview ? URL.createObjectURL(file) : null,
                    },
                };
            });
        }
    };

    const handleDeleteFile = (questionId) => {
        setFileUploadHint((prev) => {
            const next = { ...prev };
            delete next[questionId];
            return next;
        });
        setSelectedFiles(prev => {
            const newState = { ...prev };
            if (newState[questionId]?.preview) {
                URL.revokeObjectURL(newState[questionId].preview);
            }
            delete newState[questionId];
            return newState;
        });
    };

    // Handle Text Change
    const handleTextChange = (value, questionId) => {
        const isDueDatePassed = checkDueDatePassed(dueDate);

        if (!submittedQuestions.has(questionId) && !isDueDatePassed) {
            setAnswerTexts(prev => ({
                ...prev,
                [questionId]: value
            }));
        }
    };

    const handleSubmitAnswer = async (question) => {
        if (availability !== 'available' || submittedQuestions.has(question.id)) return;

        const formData = new FormData();
        formData.append('classroom_id', classroom_id);
        formData.append('assignment_id', assignment_id);
        formData.append('question_id', question.id);
        formData.append('user_id', user_id);
        formData.append('response_type', question.response_type);

        if (question.response_type === 'text_response') {
            formData.append('answer_text', answerTexts[question.id] || '');
        } else {
            const file = selectedFiles[question.id];
            if (file) {
                formData.append('media', file.file);
            }
        }

        try {
            const response = await submitUserClassroomAssignmentResponse(formData);

            if (response.success) {
                // Mark question as submitted locally
                setSubmittedQuestions(prev => new Set([...prev, question.id]));

                // Clear the response after submission
                setAnswerTexts(prev => {
                    const newState = { ...prev };
                    delete newState[question.id];
                    return newState;
                });
                setSelectedFiles(prev => {
                    const newState = { ...prev };
                    delete newState[question.id];
                    return newState;
                });

                // Refresh questions data to get updated status
                await fetchQuestions();
            }
        } catch (error) {
            console.error("Error submitting answer:", error);
            if (error.message?.toLowerCase().includes('not available')) {
                setAvailability('unavailable');
                setQuestions([]);
            }
        }
    };

    const getVideoProgressValue = (question) => {
        return videoProgressStatus[question.id] ?? question.video_progress ?? 0;
    };

    const handleVideoProgressUpdate = async (question, progressValue) => {

        if (!classroom_id) {
            console.warn("❌ classroom_id is missing:", classroom_id);
            return;
        }

        const isDueDatePassed = checkDueDatePassed(dueDate);
        const currentProgress = getVideoProgressValue(question);

        if (isDueDatePassed) {
            console.warn("⚠️ Due date passed, skipping API call");
            return;
        }

        if (progressValue <= currentProgress) {
            console.warn("⚠️ Progress not higher than current, skipping API call", { progressValue, currentProgress });
            return;
        }

        if (videoProgressLoading[question.id]) {
            console.warn("⚠️ Already loading, skipping API call");
            return;
        }

        try {
            setVideoProgressLoading((prev) => ({ ...prev, [question.id]: true }));

            const payload = {
                assignment_id,
                class_id: classroom_id,
                question_id: question.id,
                video_progress: progressValue
            };

            const response = await updateClassroomAssignmentVideoProgress(payload);

            // Check if API skipped the update
            if (response?.skipped) {
                console.warn("⏭️ API skipped update - Reason: Submission already completed");
                return;
            }

            const updatedProgress = response?.video_progress ?? progressValue;

            setVideoProgressStatus((prev) => ({
                ...prev,
                [question.id]: updatedProgress
            }));

            setQuestions((prev) =>
                prev.map((q) =>
                    q.id === question.id ? { ...q, video_progress: updatedProgress } : q
                )
            );
        } catch (error) {
            console.error("❌ Error updating video progress:", error);
        } finally {
            setVideoProgressLoading((prev) => {
                const newState = { ...prev };
                delete newState[question.id];
                return newState;
            });
        }
    };

    // Handle automatic video progress tracking
    const handleVideoTimeUpdate = (question) => ({ currentTime, duration }) => {
        if (!duration) {
            return;
        }

        const progressPercentage = Math.round((currentTime / duration) * 100);

        // Define milestones to track
        const milestones = [30, 60, 100];

        // Check if we've reached a new milestone
        for (const milestone of milestones) {
            if (progressPercentage >= milestone) {
                const milestoneKey = `${question.id}_${milestone}`;

                // Only report if this milestone hasn't been reported yet
                if (!reportedMilestones[milestoneKey]) {

                    // Mark this milestone as reported
                    setReportedMilestones((prev) => ({
                        ...prev,
                        [milestoneKey]: true
                    }));

                    // Call the API to update progress
                    handleVideoProgressUpdate(question, milestone);

                } else {
                }
            }
        }
    };

    const renderVideoProgressSection = (question) => {
        return null;
    };

    const renderQuestionMedia = (question) => {
        switch (question.question_type) {
            case 'image':
                return (
                    <div className="tw-mb-4">
                        <img
                            src={question.media_url}
                            alt="Question"
                            className="tw-max-w-full tw-rounded-xl"
                            style={{ maxHeight: '300px' }}
                        />
                    </div>
                );
            case 'audio':
                return (
                    <div className="tw-mb-4">
                        <audio controls className="tw-w-full">
                            <source src={question.media_url} type="audio/mpeg" />
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                );
            case 'video':
                return (
                    <div className="tw-mb-4">
                        <BitmovinPlayer
                            lazy
                            src={question.media_url}
                            title={question.question || 'Assignment video'}
                            className="tw-w-full tw-rounded-xl"
                            objectFit="contain"
                            style={{ maxHeight: '400px', width: '100%' }}
                            onTimeUpdate={handleVideoTimeUpdate(question)}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    const renderFilePreview = (question) => {
        const fileData = selectedFiles[question.id];
        if (!fileData) return null;

        return (
            <div className="tw-mt-4 tw-bg-slate-50 tw-rounded-xl tw-p-2">
                {question.response_type === 'image_response' && fileData.preview && (
                    <>
                        <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                            <span className="!tw-text-[var(--secondary-color)] tw-text-slate-500">Selected Image:</span>
                            <button
                                className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-p-[8px_16px] tw-font-medium [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-border-[1px] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(254_205_211_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(190_18_60_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(255_241_242_/_var(--tw-bg-opacity,_1))] tw-rounded-[0.375rem] tw-pl-3 tw-pr-3 tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-sm ui-button-outline-danger tw-flex tw-items-center tw-gap-1"}
                                style={{
                                    width: 'auto',           // ✅ prevents full width
                                    padding: '4px 12px',
                                    fontSize: '13px',
                                    whiteSpace: 'nowrap',
                                }}
                                onClick={() => handleDeleteFile(question.id)}
                            >
                                <Icon icon="mdi:delete" width="18" height="18" />
                                Remove
                            </button>
                        </div>
                        <img
                            src={fileData.preview}
                            alt="Preview"
                            className="tw-max-w-full tw-rounded-xl"
                            style={{ maxHeight: '200px' }}
                        />
                    </>
                )}
                {question.response_type === 'audio_response' && fileData.file && (
                    <div className="tw-relative tw-bg-slate-50 tw-rounded-xl tw-p-4">
                        {/* Top-right aligned small Remove button */}
                        <div className="tw-flex tw-justify-end tw-mb-2">
                            <button
                                className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-p-[8px_16px] tw-font-medium [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-border-[1px] tw-border-[transparent] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-bg-opacity:1] tw-bg-[rgb(225_29_72_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(190_18_60_/_var(--tw-bg-opacity,_1))] tw-rounded-[0.375rem] tw-pl-3 tw-pr-3 tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-sm ui-button-danger tw-flex tw-items-center tw-gap-1"}
                                style={{
                                    width: 'auto',           // ✅ prevents full width
                                    padding: '4px 12px',
                                    fontSize: '13px',
                                    whiteSpace: 'nowrap',
                                }}
                                onClick={() => handleDeleteFile(question.id)}
                            >
                                <Icon icon="mdi:delete" width="14" height="14" />
                                Remove
                            </button>
                        </div>

                        {/* File Name */}
                        <div className="tw-flex tw-items-center tw-mb-2">
                            <Icon icon="mdi:file-music-outline" className="!tw-text-[var(--primary-color)] tw-text-brand-600 tw-me-2" width="20" height="20" />
                            <span className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-truncate" style={{ maxWidth: '250px' }}>
                                {fileData.file.name}
                            </span>
                        </div>

                        {(fileData.file.type || '').toLowerCase().startsWith('video/') ? (
                            <BitmovinPlayer src={fileData.preview} title={fileData.file.name} className="tw-w-full tw-rounded-xl" objectFit="contain" style={{ maxHeight: '220px', width: '100%' }} />
                        ) : (
                            <audio controls className="tw-w-full" key={fileData.preview}>
                                <source src={fileData.preview} type={fileData.file.type || 'audio/mpeg'} />
                                Your browser does not support the audio element.
                            </audio>
                        )}
                    </div>
                )}
                {question.response_type === 'video_response' && fileData.file && (
                    <div className="tw-relative tw-bg-slate-50 tw-rounded-xl tw-p-4">
                        {/* Top-right aligned small Remove button */}
                        <div className="tw-flex tw-justify-end tw-mb-2">
                            <button
                                className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-p-[8px_16px] tw-font-medium [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-border-[1px] tw-border-[transparent] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-bg-opacity:1] tw-bg-[rgb(225_29_72_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(190_18_60_/_var(--tw-bg-opacity,_1))] tw-rounded-[0.375rem] tw-pl-3 tw-pr-3 tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-sm ui-button-danger tw-flex tw-items-center tw-gap-1"}
                                style={{
                                    width: 'auto',
                                    padding: '4px 12px',
                                    fontSize: '13px',
                                    whiteSpace: 'nowrap',
                                }}
                                onClick={() => handleDeleteFile(question.id)}
                            >
                                <Icon icon="mdi:delete" width="14" height="14" />
                                Remove
                            </button>
                        </div>

                        {/* File Name */}
                        <div className="tw-flex tw-items-center tw-mb-2">
                            <Icon icon="mdi:video-outline" className="!tw-text-[var(--danger-color)] tw-text-rose-600 tw-me-2" width="20" height="20" />
                            <span className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-truncate" style={{ maxWidth: '250px' }}>
                                {fileData.file.name}
                            </span>
                        </div>

                        {/* Video Player */}
                        <BitmovinPlayer src={fileData.preview} title={fileData.file.name} className="tw-w-full tw-rounded-xl" objectFit="contain" style={{ maxHeight: '300px', width: '100%' }} />
                    </div>
                )}
            </div>
        );
    };

    // Handle Start Quiz button click
    const handleStartQuiz = (question) => {
        // Navigate to assignment quiz page with question ID
        const encodedQuestionId = encodeData(question.id);
        navigate(`/user/assignment-quiz/${encodedAssignmentId}/${encodedQuestionId}?classroomid=${encodedClassroomId}`);
    };

    const renderAnswerSection = (question) => {
        // Check if due date has passed
        const isDueDatePassed = checkDueDatePassed(dueDate);

        // Special handling for quiz questions
        if (question.question_type === 'quiz') {
            const quizSummary = question.quiz_attempt_summary || {};
            const hasAttempted = Boolean(question.isUserAttenedQuiz);
            const totalQuestions = quizSummary.total_questions ?? 0;
            const attemptedQuestions = quizSummary.attempted_questions ?? 0;
            const correctAnswers = Number(quizSummary.correct_answers ?? 0);
            const wrongAnswers = Number(quizSummary.wrong_answers ?? 0);
            const scorePercentage = quizSummary.percentage ?? 0;
            const passStatus = quizSummary.pass_status || 'pending';
            const passingPercentage = quizSummary.passing_percentage ?? question.passing_percentage ?? null;
            const durationMinutes = quizSummary.duration ?? question.duration ?? null;
            const earningPoints = quizSummary.earning_points ?? question.points ?? null;
            const quizInfo = [
                {
                    icon: 'mdi:help-circle-outline',
                    label: 'Total Questions',
                    value: totalQuestions
                },
                {
                    icon: 'mdi:clock-outline',
                    label: 'Duration',
                    value: durationMinutes ? `${durationMinutes} min` : '--'
                },
                {
                    icon: 'mdi:trophy-outline',
                    label: 'Passing Score',
                    value: passingPercentage !== null ? `${passingPercentage}%` : '--'
                },
                {
                    icon: 'mdi:star-outline',
                    label: 'Points',
                    value: earningPoints !== null ? `${earningPoints}` : '--'
                }
            ];
            const quizActionDisabled = isDueDatePassed;
            const ctaLabel = hasAttempted ? 'Retake Quiz' : 'Start Quiz';
            const ctaDescription = quizActionDisabled
                ? 'You cannot take the quiz after the due date.'
                : hasAttempted
                    ? 'Boost your score by attempting the quiz again.'
                    : 'Click start whenever you are ready.';

            return (
                <div className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] tw-p-4 md:tw-p-6 tw-bg-slate-50 tw-rounded-xl tw-border"}>
                    <div className="tw-flex tw-flex-col md:tw-flex-row tw-flex-wrap tw-justify-between md:tw-items-center tw-gap-4 tw-mb-4">
                        <div className="tw-grow">
                            <p className="![font-weight:var(--font-weight-semibold)] !tw-text-[var(--secondary-color)] !tw-text-[12px] tw-uppercase tw-text-slate-500 tw-text-sm tw-font-semibold tw-mb-1">
                                {hasAttempted ? 'Quiz Attempt Summary' : 'Quiz Overview'}
                            </p>
                            <h5 className="tw-mb-1">
                                {hasAttempted ? 'You can improve this score' : 'Ready to take the quiz?'}
                            </h5>
                            <p className={`!tw-text-[12px] tw-text-sm tw-mb-0   ${quizActionDisabled ? "!tw-text-[var(--danger-color)] tw-text-rose-600" : "!tw-text-[var(--secondary-color)] tw-text-slate-500"}`}>
                                {ctaDescription}
                            </p>
                        </div>
                        <div className="md:tw-ms-auto">
                            <button
                                className={`[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-border-[transparent] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] ui-button   ${hasAttempted ? "[--tw-bg-opacity:1] tw-bg-[rgb(254_243_199_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(120_53_15_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(253_230_138_/_var(--tw-bg-opacity,_1))] ui-button-warning" : "[&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button-primary"}   tw-inline-flex tw-items-center tw-gap-2 tw-px-6`}
                                style={{ minWidth: '180px' }}
                                onClick={() => handleStartQuiz(question)}
                                disabled={quizActionDisabled}
                            >
                                <Icon icon="mdi:play-circle" width="20" height="20" />
                                {ctaLabel}
                            </button>
                        </div>
                    </div>

                    <div className={"tw-flex tw-flex-wrap tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] [--grid-x:1rem] [--grid-y:1rem] ui-row tw-gap-grid-3 tw-mb-2"}>
                        {quizInfo.map((item) => (
                            <div className="tw-min-w-[0px] ui-column tw-w-1/2 lg:tw-w-1/4" key={item.label}>
                                <div className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] tw-h-full tw-rounded-xl tw-border tw-bg-white tw-p-4"}>
                                    <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-flex tw-items-center tw-gap-2 tw-text-slate-500 tw-text-sm tw-uppercase tw-mb-1">
                                        <Icon icon={item.icon} width="16" height="16" />
                                        {item.label}
                                    </div>
                                    <div className="![font-weight:var(--font-weight-semibold)] tw-font-semibold tw-text-lg">{item.value}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {!hasAttempted && (
                        <div className="tw-rounded-[0.75rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(224_242_254_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(240_249_255_/_var(--tw-bg-opacity,_1))] tw-p-4 tw-text-[0.875rem] tw-leading-[1.25rem] [--tw-text-opacity:1] tw-text-[rgb(12_74_110_/_var(--tw-text-opacity,_1))] ui-alert ui-alert-info tw-flex tw-items-start tw-gap-2 tw-py-2 tw-mb-0">
                            <Icon icon="mdi:information-outline" width="18" height="18" className="tw-mt-1" />
                            <div>
                                <strong className="tw-block">You have not attempted this quiz yet.</strong>
                                <span className="!tw-text-[12px] tw-text-sm">Start the quiz to earn points and track your progress.</span>
                            </div>
                        </div>
                    )}

                    {hasAttempted && (
                        <>
                            <hr className="tw-my-6" />
                            <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-start tw-gap-4 tw-mb-4">
                                <div>
                                    <p className="![font-weight:var(--font-weight-semibold)] !tw-text-[var(--secondary-color)] !tw-text-[12px] tw-uppercase tw-text-slate-500 tw-text-sm tw-font-semibold tw-mb-1">Last Attempt</p>
                                    <h5 className="tw-mb-1">Score: {scorePercentage}%</h5>
                                    <p className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm tw-mb-0">
                                        {passStatus === 'pass'
                                            ? 'Great job! You passed this quiz.'
                                            : 'Keep trying to meet the passing score.'}
                                    </p>
                                </div>
                                <StatusBadge tone={passStatus === 'pass' ? 'success' : passStatus === 'fail' ? 'danger' : 'warning'}>
                                    <Icon
                                        icon={
                                            passStatus === 'pass'
                                                ? 'mdi:check-circle'
                                                : passStatus === 'fail'
                                                    ? 'mdi:close-circle'
                                                    : 'mdi:clock-outline'
                                        }
                                        width="16"
                                        height="16"
                                    />
                                    {passStatus === 'pass'
                                        ? 'Passed'
                                        : passStatus === 'fail'
                                            ? 'Failed'
                                            : 'Pending'}
                                </StatusBadge>
                            </div>

                            <div className={"tw-flex tw-flex-wrap tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] [--grid-x:1rem] [--grid-y:1rem] ui-row tw-gap-grid-3"}>
                                {[
                                    {
                                        icon: 'mdi:clipboard-text-outline',
                                        label: 'Attempted',
                                        value: attemptedQuestions
                                    },
                                    {
                                        icon: 'mdi:check-circle',
                                        label: 'Correct',
                                        value: correctAnswers
                                    },
                                    {
                                        icon: 'mdi:close-circle',
                                        label: 'Wrong',
                                        value: wrongAnswers
                                    },
                                    {
                                        icon: 'mdi:percent-outline',
                                        label: 'Accuracy',
                                        value: `${scorePercentage}%`
                                    }
                                ].map((item) => (
                                    <div className="tw-min-w-[0px] ui-column tw-w-1/2 lg:tw-w-1/4" key={item.label}>
                                        <div className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] tw-h-full tw-rounded-xl tw-border tw-bg-white tw-p-4"}>
                                            <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-flex tw-items-center tw-gap-2 tw-text-slate-500 tw-text-sm tw-uppercase tw-mb-1">
                                                <Icon icon={item.icon} width="16" height="16" />
                                                {item.label}
                                            </div>
                                            <div className="![font-weight:var(--font-weight-semibold)] tw-font-semibold tw-text-lg">{item.value}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {quizActionDisabled && (
                        <div className="tw-rounded-[0.75rem] tw-border-[1px] tw-p-4 tw-text-[0.875rem] tw-leading-[1.25rem] tw-bg-[rgb(255_251_235_/_var(--tw-bg-opacity,_1))] tw-border-[rgb(254_243_199_/_var(--tw-border-opacity,_1))] [--tw-border-opacity:1] [--tw-bg-opacity:1] [--tw-text-opacity:1] tw-text-[rgb(120_53_15_/_var(--tw-text-opacity,_1))] ui-alert ui-alert-danger tw-flex tw-items-start tw-gap-2 tw-py-2 tw-mt-4 tw-mb-0">
                            <Icon icon="mdi:alert-circle-outline" width="18" height="18" className="tw-mt-1" />
                            <div>
                                <strong className="tw-block">Quiz time over.</strong>
                                <span className="!tw-text-[12px] tw-text-sm">The due date for this quiz has passed, so you cannot start it now.</span>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        // Regular question handling (non-quiz)
        if (question.is_completed === 1 || submittedQuestions.has(question.id)) {
            return (
                <div className="tw-p-4 tw-bg-slate-50 tw-rounded-xl">
                    <div className="tw-flex tw-items-center tw-mb-2">
                        <StatusBadge tone="success">
                            <Icon icon="mdi:check-circle" width="14" height="14" />
                            Response Submitted
                        </StatusBadge>
                    </div>
                    {question.answer_text && (
                        <p className="tw-mb-0">{question.answer_text}</p>
                    )}
                    {question.submission_media_url && (
                        question.submitted_response_type === 'image_response' ? (
                            <img
                                src={question.submission_media_url}
                                alt="Submitted Answer"
                                className="tw-max-w-full tw-rounded-xl tw-mt-2"
                                style={{ maxHeight: '200px' }}
                            />
                        ) : question.submitted_response_type === 'video_response' ? (
                            <BitmovinPlayer lazy src={question.submission_media_url} title="Submitted video answer" className="tw-w-full tw-rounded-xl tw-mt-2" objectFit="contain" style={{ maxHeight: '300px', width: '100%' }} />
                        ) : (
                            <audio controls className="tw-w-full tw-mt-2">
                                <source src={question.submission_media_url} type="audio/mpeg" />
                                Your browser does not support the audio element.
                            </audio>
                        )
                    )}
                </div>
            );
        }

        if (isDueDatePassed) {
            return (
                <div className="tw-p-4 tw-bg-slate-50 tw-rounded-xl">
                    <div className="tw-flex tw-items-center tw-mb-2">
                        <StatusBadge tone="danger">
                            <Icon icon="mdi:clock-alert" width="14" height="14" />
                            Submission Time Over
                        </StatusBadge>
                    </div>
                    <p className="!tw-text-[var(--danger-color)] !tw-text-[12px] tw-text-rose-600 tw-text-sm tw-mb-0">
                        You cannot submit answers after the due date. The submission deadline has passed.
                    </p>
                </div>
            );
        }

        return (
            <div className="answer-section">
                {question.response_type === 'text_response' && (
                    <div className="tw-mb-4">
                        <textarea
                            className={"[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input"}
                            rows="3"
                            placeholder="Type your answer here..."
                            value={answerTexts[question.id] || ''}
                            onChange={(e) => handleTextChange(e.target.value, question.id)}
                            disabled={submittedQuestions.has(question.id) || isDueDatePassed}
                        ></textarea>
                    </div>
                )}

                {(question.response_type === 'image_response' || question.response_type === 'audio_response' || question.response_type === 'video_response') && (
                    <div className="tw-flex tw-justify-start">
                        <div className="tw-min-w-[0px] ui-column tw-w-full md:tw-w-1/4">
                            <div className="tw-flex tw-gap-2 tw-mb-4">
                                <button
                                    className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-border-[1px] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [[data-theme='dark']_&]:tw-border-[#4895ef] [[data-theme='dark']_&]:tw-text-[#4895ef] [[data-theme='dark']_&:hover]:tw-bg-[#4895ef] [[data-theme='dark']_&:hover]:tw-text-[white] tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] tw-border-[rgb(199_210_254_/_var(--tw-border-opacity,_1))] tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-border-opacity:1] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] [&:hover]:tw-text-[white] [&:hover]:tw-bg-[#4169E1] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] ui-button ui-button-outline-primary tw-flex tw-items-center tw-gap-2"}
                                    onClick={() => document.getElementById(`file-upload-${question.id}`).click()}
                                    disabled={submittedQuestions.has(question.id) || selectedFiles[question.id] || isDueDatePassed}
                                >
                                    <Icon
                                        icon={question.response_type === 'image_response' ? 'mdi:image' :
                                              question.response_type === 'audio_response' ? 'mdi:microphone' :
                                              'mdi:video'}
                                        width="20"
                                        height="20"
                                    />
                                    Upload {question.response_type === 'image_response' ? 'Image' :
                                             question.response_type === 'audio_response' ? 'Audio' :
                                             'Video'}
                                    <input
                                        id={`file-upload-${question.id}`}
                                        type="file"
                                        accept={question.response_type === 'image_response' ? 'image/*' :
                                                question.response_type === 'audio_response' ? 'audio/*,.mp4,.m4a,.aac,.wav,.ogg,.webm' :
                                                'video/*'}
                                        onChange={(e) => handleFileUpload(e,
                                                  question.response_type === 'image_response' ? 'image' :
                                                  question.response_type === 'audio_response' ? 'audio' :
                                                  'video', question.id)}
                                        style={{ display: 'none' }}
                                        disabled={submittedQuestions.has(question.id) || selectedFiles[question.id] || isDueDatePassed}
                                    />
                                </button>
                            </div>
                            {fileUploadHint[question.id] && (
                                <div className="!tw-text-[12px] tw-rounded-[0.75rem] tw-border-[1px] tw-p-4 [--tw-border-opacity:1] tw-border-[rgb(254_243_199_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_251_235_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(120_53_15_/_var(--tw-text-opacity,_1))] ui-alert ui-alert-warning tw-py-2 tw-px-4 tw-text-sm tw-mb-0 tw-mt-2" role="alert">
                                    {fileUploadHint[question.id]}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* File Preview Section */}
                {renderFilePreview(question)}

                <div className="tw-flex tw-justify-end tw-mt-4">
                    <div className="tw-w-full sm:tw-w-auto">
                        <button
                            type="button"
                            style={{ minWidth: '150px', width: '100%', whiteSpace: 'nowrap' }}
                            className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] tw-rounded-[0.375rem] tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-primary ui-button-sm tw-px-6 tw-w-full tw-whitespace-nowrap"}
                            onClick={() => handleSubmitAnswer(question)}
                            disabled={
                                submittedQuestions.has(question.id) ||
                                isDueDatePassed ||
                                (question.response_type !== 'text_response' && !selectedFiles[question.id]) ||
                                (question.response_type === 'text_response' && !answerTexts[question.id])
                            }
                        >
                            Submit Answer
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    if (availability !== 'available') {
        return (
            <div className="tw-mx-auto tw-max-w-xl tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-8 tw-text-center">
                <h4 className="tw-mb-2">{availability === 'checking' ? 'Loading assignment' : 'Assignment not available'}</h4>
                {availability === 'unavailable' && <p className="tw-mb-4">This assignment is hidden or you no longer have access to it.</p>}
                {availability === 'unavailable' && <button className="ui-button ui-button-primary" onClick={() => navigate(-1)}>Back to assignments</button>}
            </div>
        );
    }

    return (
        <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
            {/* Header with Question Stats */}
            <div className="tw-flex tw-flex-wrap md:tw-flex-row tw-justify-between tw-items-center tw-mb-6">
                <h4 className="tw-mb-0">Assignment Questions</h4>
                <div className="tw-flex tw-gap-2 tw-flex-wrap tw-items-center">
                    <StatusBadge tone="info">
                        <Icon icon="mdi:calendar" width="14" height="14" />
                        Due: {formatDueDate(dueDate)}
                    </StatusBadge>
                    <StatusBadge tone="info">
                        <Icon icon="mdi:help-circle" width="14" height="14" />
                        {questions.length} Questions
                    </StatusBadge>
                    <StatusBadge tone="success">
                        <Icon icon="mdi:check-circle" width="14" height="14" />
                        {questions.filter(q => q.is_completed === 1).length} Submitted
                    </StatusBadge>
                    <StatusBadge tone="warning">
                        <Icon icon="mdi:clock" width="14" height="14" />
                        {questions.filter(q => q.is_completed === 0).length} Remaining
                    </StatusBadge>
                </div>
            </div>

            {/* No Questions State */}
            {questions.length === 0 && (
                <div className="max-[768px]:!tw-w-[50%] tw-min-w-[0px] ui-column md:tw-w-1/2 tw-mx-auto">
                    <div className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card tw-shadow-sm tw-border-0"}>
                        <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-p-5 tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body">
                            <NoData
                                message="No Assignment Questions Available"
                                description="The instructor hasn't added any questions to this assignment yet."
                                icon="mdi:file-document-outline"
                            />
                            <div className="tw-text-center tw-pb-6">
                                <button
                                    className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary tw-flex tw-items-center tw-gap-2 tw-mx-auto"}
                                    onClick={() => window.history.back()}
                                >
                                    <Icon icon="mdi:arrow-left" width="20" height="20" />
                                    Back to Assignment
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Questions List */}
            {questions.length > 0 && questions.map((question) => (
                <div key={question.id} className={"[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] [&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card tw-mb-6 tw-border tw-shadow-sm"}>
                    <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-p-5 tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body">
                        <div className="tw-flex tw-justify-between tw-items-start tw-mb-6">
                            <h5 className="tw-text-[1rem] tw-font-semibold tw-text-[#2c3e50] [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] tw-overflow-hidden tw-leading-[1.5rem] ui-card-title tw-mb-0">Question {question.question_order}</h5>
                            {question.is_completed === 1 && (
                                <StatusBadge tone={question.answer_status === 'right' ? 'success' : question.answer_status === 'wrong' ? 'danger' : 'warning'}>
                                    <Icon
                                        icon={
                                            question.answer_status === 'right' ? 'mdi:check-circle' :
                                            question.answer_status === 'wrong' ? 'mdi:close-circle' :
                                            'mdi:clock-outline'
                                        }
                                        width="14"
                                        height="14"
                                    />
                                    {question.answer_status === 'right' ? 'Correct' :
                                     question.answer_status === 'wrong' ? 'Incorrect' :
                                     'Pending Review'}
                                </StatusBadge>
                            )}
                        </div>

                        <div className="tw-mb-6">
                            <p className="tw-text-[0.875rem] tw-leading-[1.25rem] [--tw-text-opacity:1] tw-text-[rgb(71_85_105_/_var(--tw-text-opacity,_1))] ui-card-text">{question.question_text}</p>
                            {renderQuestionMedia(question)}
                            {question.question_type === 'video' && (
                                <div className="tw-mt-4">
                                    {renderVideoProgressSection(question)}
                                </div>
                            )}
                        </div>

                        <div>
                            <h6 className="tw-mb-4">
                                {question.question_type === 'quiz' ? 'Quiz Action' : 'Your Response'}
                            </h6>
                            {renderAnswerSection(question)}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default AssignmentQuestions;
